// src/firebaseRestApi.js
/**
 * Firebase REST API helpers for more reliable multiplayer communication
 * This provides fallback methods when WebSocket connections are unreliable
 */

// firebaseRestApi.js
import { firebaseConfig, auth, db } from './firebaseConfig.js';
import { hasSpecialAccess, getSpecialAccessUserId, getSpecialAccessDisplayName } from './services/specialAccessService.js';

// Add at the top of firebaseRestApi.js, after the first comment
console.log('firebaseRestApi.js loading...');
// Format the database URL
/**
 * Get the Firebase Realtime Database URL with proper formatting
 * @returns {string} Formatted database URL
 */
export function getDbUrl() {
  // SECURITY: No hardcoded fallback - must use environment variable
  let dbUrl = firebaseConfig.databaseURL;
  
  if (!dbUrl) {
    throw new Error('FIREBASE_DATABASE_URL environment variable is required');
  }
  
  // Ensure URL uses HTTPS
  if (dbUrl.startsWith('http:')) {
    dbUrl = dbUrl.replace('http:', 'https:');
  }
  
  // Ensure URL ends with /
  if (!dbUrl.endsWith('/')) {
    dbUrl += '/';
  }
  
  return dbUrl;
}

// Update the createGame function
// --- REPLACE THIS FUNCTION ---
// --- REPLACE createGame FUNCTION ---
export async function createGame(userId, hostDisplayNameArg, gameMode = 'suddenDeath', timeLimit = 300) {
  const specialAccess = hasSpecialAccess();
  let isGuestPlus = false;
  let finalUserId = userId;
  let finalHostDisplayName = hostDisplayNameArg;

  if (!finalUserId && specialAccess) {
    console.log("REST: Creating game for Guest+ user");
    finalUserId = getSpecialAccessUserId();
    finalHostDisplayName = getSpecialAccessDisplayName();
    isGuestPlus = true;
  }

  if (!finalUserId) throw new Error('User ID or Guest+ access is required to create a game');

  // --- Fetch Display Name for Auth Users ---
  if (auth.currentUser && !isGuestPlus && finalUserId === auth.currentUser.uid) {
    try {
      const profileDoc = await db.collection("userProfiles").doc(finalUserId).get();
      if (profileDoc.exists && profileDoc.data().displayName) {
        finalHostDisplayName = profileDoc.data().displayName;
      } else if (!finalHostDisplayName) { // Fallback if arg was also null/empty
         finalHostDisplayName = auth.currentUser.email || "Player";
      }
    } catch (err) {
       console.warn(`REST createGame: Error fetching profile, using: ${finalHostDisplayName}`, err);
       if (!finalHostDisplayName) finalHostDisplayName = auth.currentUser.email || "Player";
    }
  } else if (!finalHostDisplayName) { // Ensure Guest+ has a name
      finalHostDisplayName = "Player";
  }
  // --- End Fetch Display Name ---

  const gameCode = generateGameCode(); // Assumes generateGameCode() is defined in this file or imported
  const now = Date.now();
  const gameData = {
    gameCode, hostId: finalUserId, gameMode, timeLimit, status: 'waiting',
    createdAt: now, // Use local timestamp for REST creation
    lastUpdated: now,
    isGuestPlusGame: isGuestPlus,
    players: {
      [finalUserId]: {
        name: finalHostDisplayName, // Use fetched/provided name
        isHost: true, joinedAt: now, status: 'waiting',
        score: 0, lives: 3, isAlive: true, position: { x: 0, y: 0 },
        isGuestPlus: isGuestPlus
      }
    },
    playerCount: 1
  };

  const url = `${getDbUrl()}games/${gameCode}.json`; // Assumes getDbUrl() is defined
  console.log(`REST createGame: Creating game at URL: ${url} with host ${finalUserId} (${finalHostDisplayName})`);

  // --- Authenticated PUT Request ---
  let idToken = null;
  try {
      if(auth.currentUser) idToken = await auth.currentUser.getIdToken();
  } catch(tokenError){ console.warn("REST createGame: Could not get auth token.", tokenError); }

  const requestOptions = {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(gameData)
  };
  const finalUrl = idToken ? `${url}?auth=${idToken}` : url;
  // --- End Authenticated PUT ---

  const response = await fetch(finalUrl, requestOptions);

  if (!response.ok) {
      const errorText = await response.text();
      console.error(`REST createGame Error (${response.status}): ${errorText}`);
      throw new Error(`Failed to create game via REST: ${response.statusText} - ${errorText.substring(0,100)}`);
  }
  console.log(`REST createGame: Game ${gameCode} created successfully.`);
  return gameCode;
}

/**
 * Join a game using REST API
 * @param {string} gameCode - Game code to join
 * @param {string} userId - User ID
 * @param {string} displayName - User display name
 */
// In firebaseRestApi.js - Modify the joinGame function
// Find the function and add profile check for display name:

// --- REPLACE THIS FUNCTION ---
// --- REPLACE joinGame FUNCTION ---
export async function joinGame(gameCode, userId, joinerDisplayNameArg) {
  const specialAccess = hasSpecialAccess();
  let isGuestPlus = false;
  let finalUserId = userId;
  let finalJoinerDisplayName = joinerDisplayNameArg;

  if (!finalUserId && specialAccess) {
    console.log("REST: Joining game as Guest+ user");
    finalUserId = getSpecialAccessUserId();
    finalJoinerDisplayName = getSpecialAccessDisplayName();
    isGuestPlus = true;
  }

  if (!gameCode || !finalUserId) throw new Error('Game code and user ID/Guest+ access are required');

  // --- Fetch Display Name for Auth Users ---
  if (auth.currentUser && !isGuestPlus && finalUserId === auth.currentUser.uid) {
      try {
          const profileDoc = await db.collection("userProfiles").doc(finalUserId).get();
          if (profileDoc.exists && profileDoc.data().displayName) {
              finalJoinerDisplayName = profileDoc.data().displayName;
          } else if (!finalJoinerDisplayName) {
              finalJoinerDisplayName = auth.currentUser.email || "Player";
          }
      } catch (err) {
           console.warn(`REST joinGame: Error fetching profile`, err);
           if (!finalJoinerDisplayName) finalJoinerDisplayName = auth.currentUser.email || "Player";
      }
  } else if (!finalJoinerDisplayName) {
       finalJoinerDisplayName = "Player";
  }
  // --- End Fetch Display Name ---


  // --- Authenticated Read (GET) to check game status ---
  let idToken = null;
  try {
      if(auth.currentUser) idToken = await auth.currentUser.getIdToken();
  } catch(tokenError){ console.warn("REST joinGame: Could not get auth token for initial read.", tokenError); }

  const gameUrl = `${getDbUrl()}games/${gameCode}.json`;
  const gameReadUrl = idToken ? `${gameUrl}?auth=${idToken}` : gameUrl;
  const gameResponse = await fetch(gameReadUrl);

  if (!gameResponse.ok) {
       if(gameResponse.status === 404 || gameResponse.status === 401) throw new Error('Game not found or permission denied.');
       throw new Error(`Error checking game status via REST: ${gameResponse.statusText}`);
  }
  const game = await gameResponse.json();
  if (!game) throw new Error('Game not found.');
  if (game.status !== 'waiting') throw new Error('Game has already started or ended.');
  if (game.players && game.players[finalUserId]) {
      console.warn(`REST joinGame: User ${finalUserId} already in game ${gameCode}. Rejoining.`);
      // Optionally update lastSeen via PATCH here if needed
      return true;
  }
  // --- End Authenticated Read ---

  const now = Date.now();
  const playerData = {
    name: finalJoinerDisplayName,
    isHost: false, joinedAt: now, status: 'waiting',
    score: 0, lives: 3, isAlive: true, position: { x: 0, y: 0 },
    lastUpdated: now, // Use local time for REST
    isGuestPlus: isGuestPlus
  };

  // --- Authenticated Write (PUT or PATCH) ---
  // PUT player data
  const playerUrl = `${getDbUrl()}games/${gameCode}/players/${finalUserId}.json`;
  const playerWriteUrl = idToken ? `${playerUrl}?auth=${idToken}` : playerUrl;
  const playerResponse = await fetch(playerWriteUrl, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(playerData)
  });
  if (!playerResponse.ok) {
       const errorText = await playerResponse.text();
       console.error(`REST joinGame Error Adding Player (${playerResponse.status}): ${errorText}`);
       throw new Error(`Error adding player via REST: ${playerResponse.statusText}`);
  }

  // PATCH player count and timestamp (less critical if it fails)
  const updateUrl = `${getDbUrl()}games/${gameCode}.json`;
  const updateWriteUrl = idToken ? `${updateUrl}?auth=${idToken}` : updateUrl;
  const updateResponse = await fetch(updateWriteUrl, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerCount: (game.playerCount || 0) + 1, lastUpdated: now })
  });
  if (!updateResponse.ok) console.warn(`REST joinGame: Error updating game count/timestamp: ${updateResponse.statusText}`);
  // --- End Authenticated Write ---

  console.log(`REST joinGame: User ${finalUserId} (${finalJoinerDisplayName}) joined game ${gameCode}.`);
  // Optionally add a game event here using addGameEvent REST call
  // await addGameEvent(gameCode, `${finalJoinerDisplayName} joined the game!`, "#00FF00");
  return true;
}

// Poll for game updates instead of using real-time listeners
// In firebaseRestApi.js, find and update the pollGameUpdates function:


// --- REPLACE THE ENTIRE pollGameUpdates FUNCTION WITH THIS VERSION ---

// --- END REPLACEMENT ---
// --- END REPLACEMENT ---
// --- END REPLACEMENT ---
// --- REPLACE pollGameUpdates FUNCTION ---
// (Use the robust version provided previously in the prompt analysis - includes retries, timeouts, 401 handling)
export function pollGameUpdates(gameCode, callback, interval = 1500) {
  // ... (Implementation from the thought process section 5, subsection 3) ...
if (!gameCode || typeof callback !== 'function') {
  console.error('[pollGameUpdates] Game code and callback required.');
  return () => {};
}
console.log(`[pollGameUpdates] Starting for ${gameCode} with interval ${interval}ms`);

let lastEtag = null;
let polling = true;
let timeoutId = null;
let consecutiveErrors = 0;
const MAX_CONSECUTIVE_ERRORS = 10; // Increased slightly
let forceTokenRefresh = false; // Flag to force refresh after a 401
let currentInterval = interval; // Allow interval to change

const fetchWithTimeout = async (url, options = {}, timeout = 8000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      // Don't log token here
      const safeUrl = url.split('?')[0] + (url.includes('auth=') ? '?auth=...' : '');
      console.warn(`[pollGameUpdates] Fetch timed out for ${safeUrl}`);
    }
    throw error; // Re-throw other errors
  }
};

const pollOnce = async () => {
  if (!polling) return;

  let idToken = null;
  try {
    const user = auth.currentUser;
    if (user) {
      // Only force refresh if the flag is explicitly true
      idToken = await user.getIdToken(forceTokenRefresh);
      forceTokenRefresh = false; // Reset flag after attempt
      if (!idToken) console.warn(`[pollGameUpdates] user.getIdToken() returned null/undefined for ${gameCode}`);
    } else {
      // console.log("[pollGameUpdates] No authenticated user for polling."); // Less verbose
    }
  } catch (tokenError) {
    console.error(`[pollGameUpdates] Error getting auth token for ${gameCode}:`, tokenError);
    // If token error persists, could indicate auth issue
    if (tokenError.code === 'auth/user-token-expired' || tokenError.code === 'auth/invalid-user-token') {
        forceTokenRefresh = true; // Try forcing refresh next time
    }
  }

  const baseUrl = `${getDbUrl()}games/${gameCode}.json`;
  let urlWithAuth = baseUrl;
   // Only add auth token if we successfully got one
  if (idToken) {
      // Use URLSearchParams for safer construction
      const url = new URL(baseUrl);
      url.searchParams.append('auth', idToken);
      urlWithAuth = url.toString();
  }
  // --- Log URL without token in production ---
  // const safeLogUrl = urlWithAuth.split('?')[0] + (urlWithAuth.includes('auth=') ? '?auth=...' : '');
  // console.debug(`[pollGameUpdates] Polling URL: ${safeLogUrl}`);

  try {
    const fetchOptions = { method: 'GET', headers: {} };
    if (lastEtag) fetchOptions.headers['If-None-Match'] = lastEtag;

    const response = await fetchWithTimeout(urlWithAuth, fetchOptions);

    if (response.headers.get('ETag')) lastEtag = response.headers.get('ETag');

    if (response.status === 304) { // Not Modified
      // console.debug(`[pollGameUpdates] Data not modified for ${gameCode} (304)`); // Less verbose
      consecutiveErrors = 0; // Reset errors on success/no change
       currentInterval = interval; // Reset to default interval
    } else if (response.ok) { // 200 OK
      consecutiveErrors = 0; // Reset errors
       currentInterval = interval; // Reset interval
      const data = await response.json();
      // Basic validation
      if (data && typeof data === 'object') {
           callback(data);
      } else {
           console.warn(`[pollGameUpdates] Received non-object data for ${gameCode}:`, data);
           callback({ error: "INVALID_DATA", message: "Received invalid data format." });
      }
    } else { // Handle Errors
        consecutiveErrors++;
        const status = response.status;
        let errorBodyText = '';
        try { errorBodyText = await response.text(); } catch(e){}
        console.error(`[pollGameUpdates] Error for ${gameCode}: ${status} ${response.statusText}. Body: ${errorBodyText.substring(0, 150)}`);

        if (status === 401 || status === 403) { // Unauthorized or Forbidden
           callback({ error: "AUTH_ERROR", status: status, message: "Permission denied. Retrying with refreshed token..." });
           forceTokenRefresh = true; // Force refresh next time
           currentInterval = Math.min(interval * 2, 5000); // Slow down polling on auth errors
        } else if (status === 404) { // Not Found
           callback(null); // Signal game not found
           polling = false; // Stop polling
           console.log(`[pollGameUpdates] Game ${gameCode} not found (404). Stopping poll.`);
           return; // Exit poll loop
        } else { // Other server errors (5xx, etc.)
           callback({ error: "SERVER_ERROR", status: status, message: response.statusText });
           currentInterval = Math.min(interval * 1.5, 4000); // Slightly slow down
        }
    }
  } catch (error) { // Network errors, timeouts
      consecutiveErrors++;
      console.error(`[pollGameUpdates] Network error or timeout polling ${gameCode}:`, error);
      callback({ error: "NETWORK_ERROR", message: error.message || "Network error or timeout" });
      currentInterval = Math.min(interval * 1.5, 4000); // Slightly slow down
  }

  if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
      console.error(`[pollGameUpdates] Stopping polling for ${gameCode} after ${consecutiveErrors} consecutive errors.`);
      polling = false;
      if (typeof window?.showGeneralMessage === 'function') {
           window.showGeneralMessage("Lost connection to lobby.", 5000, true);
      }
      callback({ error: "POLLING_FAILED", message: `Stopped polling after ${consecutiveErrors} errors.`});
      return; // Exit poll loop
  }

  if (polling) {
    timeoutId = setTimeout(pollOnce, currentInterval); // Use potentially adjusted interval
  }
};

pollOnce(); // Start the first poll

// Return the unsubscribe function
return () => {
  if (polling) {
      console.log(`[pollGameUpdates] Stopping polling for ${gameCode}`);
      polling = false;
      if (timeoutId) clearTimeout(timeoutId);
  }
};
}
// --- END REPLACEMENT ---

/**
 * Get a game by code using REST API
 * @param {string} gameCode - Game code
 * @returns {Promise<Object>} Game data
 */
export async function getGame(gameCode) {
  if (!gameCode) {
    throw new Error('Game code is required');
  }
  
  try {
    // Try main path first
    const mainUrl = `${getDbUrl()}games/${gameCode}.json`;
    const response = await fetch(mainUrl);
    
    if (response.ok) {
      return await response.json();
    } else if (response.status === 404) {
      // Try backup path
      const backupUrl = `${getDbUrl()}simpleGames/${gameCode}.json`;
      const backupResponse = await fetch(backupUrl);
      
      if (backupResponse.ok) {
        return await backupResponse.json();
      } else {
        return null;
      }
    } else {
      throw new Error(`Error fetching game: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error getting game:', error);
    throw error;
  }
  
}

/**
 * Leave a game using REST API
 * @param {string} gameCode - Game code
 * @param {string} userId - User ID
 */
export async function leaveGame(gameCode, userId) {
   // Support for Guest+ users
   const specialAccess = hasSpecialAccess();
   if (!userId && specialAccess) {
     userId = getSpecialAccessUserId();
   }
  if (!gameCode || !userId) {
    console.error('Game code and user ID are required');
    return;
  }
  
  try {
    // Get game data
    const game = await getGame(gameCode);
    
    if (!game) {
      return; // Game doesn't exist
    }
    
    // Get player data
    const playerData = game.players?.[userId];
    if (!playerData) {
      return; // Player not in game
    }
    
    // Check if player is host
    if (game.hostId === userId) {
      // End game if host leaves
      const updateUrl = `${getDbUrl()}games/${gameCode}.json`;
      await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'ended',
          endedAt: Date.now(),
          lastUpdated: Date.now(),
          endReason: 'hostLeft'
        })
      });
      
      // Add game event
      await addGameEvent(gameCode, `${playerData.name || 'Host'} left and ended the game`, "#FF0000");
    } else {
      // Remove player
      const playerUrl = `${getDbUrl()}games/${gameCode}/players/${userId}.json`;
      await fetch(playerUrl, {
        method: 'DELETE'
      });
      
      // Update player count
      const updateUrl = `${getDbUrl()}games/${gameCode}.json`;
      await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          playerCount: Math.max(0, (game.playerCount || 1) - 1),
          lastUpdated: Date.now()
        })
      });
      
      // Add game event
      await addGameEvent(gameCode, `${playerData.name || 'A player'} left the game`, "#FF5555");
    }
    
    return true;
  } catch (error) {
    console.error('Error leaving game:', error);
    return false;
  }
}

/**
 * Add a game event to be broadcast to all players
 * @param {string} gameCode - Game code
 * @param {string} message - Event message
 * @param {string} color - Event color (hex)
 * @param {boolean} important - Whether this is an important event
 */
// In firebaseRestApi.js - Update addGameEvent function
// --- Replace the existing addGameEvent function ---
export async function addGameEvent(gameCode, message, color = "#FFFFFF", important = false) {
  if (!gameCode || !message) {
      console.error('[addGameEvent] Game code and message are required');
      return false;
  }

  let idToken = null;
  let canWrite = false; // Flag to determine if we should attempt write

  try {
      const user = auth.currentUser;
      const specialAccess = hasSpecialAccess();

      if (user) {
          idToken = await user.getIdToken();
          if (!idToken) console.warn("[addGameEvent] Failed to get ID token for authenticated user.");
          canWrite = true; // Authenticated users can write
      } else if (specialAccess) {
          console.log("[addGameEvent] Guest+ user adding event (no ID token, rules must allow).");
          // Determine if Guest+ *should* be allowed to write events based on your rules.
          // For now, let's assume Guest+ CAN write events if configured in rules.
           canWrite = true;
      } else {
          console.warn("[addGameEvent] Cannot add event: No logged-in user or Guest+ access.");
          return false;
      }

      if (!canWrite) {
           console.warn("[addGameEvent] Write attempt skipped based on user status/rules.");
           return false;
      }

      const eventId = `evt_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      const eventData = {
          id: eventId,
          message: message.substring(0, 150), // Limit message length
          color: color,
          important: important,
          timestamp: Date.now() // Use client time for simplicity in REST
      };

      const eventsUrl = `${getDbUrl()}games/${gameCode}/events.json`;
      const urlWithAuth = idToken ? `${eventsUrl}?auth=${idToken}` : eventsUrl;

      // --- Transaction Logic using REST API ---
      // 1. Read current events (using ETag for potential optimization, though Firebase REST doesn't directly support it like RTDB transactions)
      let currentEvents = [];
      let currentEtag = null; // Firebase REST doesn't use ETags directly in this way, but we simulate the read-then-write logic

      try {
          const readResponse = await fetch(urlWithAuth); // Fetch existing data
          if (readResponse.ok) {
              const data = await readResponse.json();
              // Handle null case explicitly
              if (data === null) {
                  currentEvents = [];
              } else if (Array.isArray(data)) {
                  currentEvents = data;
              } else {
                  console.warn("[addGameEvent] Existing events data is not an array or null, resetting.");
                  currentEvents = [];
              }
               // Note: Firebase REST doesn't provide ETags easily for conditional writes like RTDB transactions.
               // We are simply reading the current state before writing the new state.
          } else if (readResponse.status !== 404) { // Don't error if path doesn't exist yet
              console.warn(`[addGameEvent] Failed to read existing events: ${readResponse.status}`);
              // Proceed with caution, might overwrite data if read failed partially
          }
      } catch (readError) {
           console.error("[addGameEvent] Network error fetching existing events:", readError);
           // Proceed, but risk overwriting concurrent changes
      }

      // 2. Prepare new events array
      const newEvents = [eventData, ...currentEvents]; // Prepend new event
      const MAX_EVENTS = 15;
      if (newEvents.length > MAX_EVENTS) {
          newEvents.length = MAX_EVENTS; // Trim older events
      }

      // 3. Write the *entire* events array back using PUT
      const writeResponse = await fetch(urlWithAuth, {
          method: 'PUT', // PUT overwrites the entire node at /events
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newEvents) // Send the whole updated array
      });

      if (!writeResponse.ok) {
          const errorText = await writeResponse.text();
          console.error(`[addGameEvent] Error saving event with PUT (${writeResponse.status}): ${errorText}`);
          throw new Error(`Failed to add game event via PUT: ${writeResponse.statusText}`);
      }

      console.log(`[addGameEvent] Event added successfully for ${gameCode}: "${message}"`);
      return true;
      // --- End Transaction Logic ---

  } catch (error) {
      console.error('[addGameEvent] Overall error:', error);
      return false;
  }
}

/**
 * Update player state using REST API
 * @param {string} gameCode - Game code
 * @param {string} userId - User ID
 * @param {Object} stateData - Player state data
 */

export async function updatePlayerState(gameCode, userId, stateData) {
  const specialAccess = hasSpecialAccess();
  let finalUserId = userId;

  if (!finalUserId && specialAccess) {
    finalUserId = getSpecialAccessUserId();
  }

  if (!gameCode || !finalUserId || !stateData) {
    // console.warn("REST updatePlayerState: Skipping - missing data."); // Reduce log noise
    return false; // Indicate failure
  }

  let idToken = null;
  try {
      if(auth.currentUser) idToken = await auth.currentUser.getIdToken();
      // Note: Guest+ users won't have an idToken, the RTDB rules must allow their writes based on their ID/flag
  } catch(tokenError){
       // Log but proceed if Guest+
       if(!specialAccess) {
            console.error("REST updatePlayerState: Failed to get auth token for non-Guest+ user.", tokenError);
            return false;
       } else {
            console.warn("REST updatePlayerState: No auth token for Guest+ user (expected).");
       }
  }

  try {
    const url = `${getDbUrl()}games/${gameCode}/players/${finalUserId}.json`;
    const writeUrl = idToken ? `${url}?auth=${idToken}` : url;

    // Add lastUpdated timestamp (using client time for REST simplicity)
    const dataToUpdate = {
      ...stateData,
      lastUpdated: Date.now()
    };

    const response = await fetch(writeUrl, {
      method: 'PATCH', // Use PATCH to only update specified fields
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dataToUpdate)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`REST Error updating player state (${response.status}) for ${finalUserId} in ${gameCode}: ${errorText}`);
       // Handle specific errors like permission denied
      if(response.status === 401 || response.status === 403) {
          console.error("Permission denied updating player state via REST.");
          // Maybe trigger a re-auth flow or notify user
      }
      return false; // Indicate failure
    }
    return true; // Indicate success
  } catch (error) {
    console.error(`REST Network Error updating player state for ${finalUserId} in ${gameCode}:`, error);
    return false; // Indicate failure
  }
}

/**
 * Check if a game exists using REST API
 * @param {string} gameCode - Game code to check
 * @returns {Promise<boolean>} Whether the game exists
 */
export async function checkGameExists(gameCode) {
  if (!gameCode) return false;
  
  try {
    const game = await getGame(gameCode);
    return game !== null;
  } catch (error) {
    console.error('Error checking if game exists:', error);
    return false;
  }
}
/**
 * Generate a random 6-character game code
 * @returns {string} Random game code
 */
// Ensure generateGameCode is defined or imported
function generateGameCode() {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 6; i++) result += characters.charAt(Math.floor(Math.random() * characters.length));
  return result;
}
// Add this at the bottom of firebaseRestApi.js
// Make functions available globally as a fallback
// Add this at the bottom of firebaseRestApi.js
// Make functions available globally as a fallback
if (typeof window !== 'undefined') {
  window.firebaseRestApi = window.firebaseRestApi || {
    getDbUrl,
    db,
    createGame,
    joinGame,
    pollGameUpdates,
    getGame,
    leaveGame,
    addGameEvent,
    updatePlayerState,
    checkGameExists
  };
}